import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowUpRight, GoogleLogo, Sparkle } from "@phosphor-icons/react";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-graphite-950 text-ink-primary" data-testid="login-page">
      {/* Left: architectural image */}
      <div className="relative hidden lg:block overflow-hidden border-r border-white/10">
        <img
          src="https://images.unsplash.com/photo-1526289034009-0240ddb68ce3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBhcmNoaXRlY3R1cmUlMjBncmFwaGl0ZSUyMHNsYXRlfGVufDB8fHx8MTc4NzgwNDYxMnww&ixlib=rb-4.1.0&q=85"
          alt="Abstract dark architecture"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-graphite-950/60" />
        <div className="absolute inset-0 blueprint-grid opacity-40" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-sm bg-white text-graphite-950 grid place-items-center font-display font-semibold">A</div>
            <span className="font-display text-lg tracking-tight">ApexGrey</span>
          </div>
          <div>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-tight max-w-md">
              Structure your business, <em className="not-italic text-ink-secondary">one signal at a time.</em>
            </h2>
            <div className="mt-8 flex items-center gap-2 text-ink-secondary text-sm">
              <Sparkle weight="duotone" className="h-4 w-4" />
              <span>Multi-tenant workspace. Leads. Quotes. Workflows.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right: sign in */}
      <div className="flex flex-col justify-center px-8 md:px-16 py-16 relative">
        <div className="lg:hidden mb-12 flex items-center gap-3">
          <div className="h-8 w-8 rounded-sm bg-white text-graphite-950 grid place-items-center font-display font-semibold">A</div>
          <span className="font-display text-lg tracking-tight">ApexGrey</span>
        </div>

        <div className="max-w-sm w-full animate-fade-up" data-testid="login-panel">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-secondary">Sign in</div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl font-light tracking-tight">
            Welcome back.
          </h1>
          <p className="mt-4 text-ink-secondary text-sm leading-relaxed">
            Continue with Google. We&rsquo;ll set up your personal workspace automatically if it&rsquo;s your first time here.
          </p>

          <button
            data-testid="login-google-button"
            onClick={handleLogin}
            className="group mt-10 w-full inline-flex items-center justify-between rounded-full bg-white text-graphite-950 px-6 py-4 font-medium transition-transform active:scale-[0.98] hover:bg-neutral-100"
          >
            <span className="inline-flex items-center gap-3">
              <GoogleLogo weight="bold" className="h-5 w-5" />
              Continue with Google
            </span>
            <ArrowUpRight weight="bold" className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>

          <div className="mt-8 text-xs text-ink-secondary/80">
            By continuing, you agree to ApexGrey&rsquo;s Terms and acknowledge our Privacy Policy.
          </div>
        </div>

        <div className="absolute bottom-6 right-8 text-[10px] uppercase tracking-widest text-ink-secondary/60">
          v0.1 · Foundation
        </div>
      </div>
    </div>
  );
}
